import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { useKbSummary } from '@/lib/api/vendor-knowledge';
import {
  B,
  DChip,
  Empty,
  Fact,
  Facts,
  Page,
  PageHead,
  Section,
  SectionHead,
  SummaryLine,
} from './kb-bits';
import { fmt, plural } from './kb-logic';
import { AnswersSection } from './answers/answers-section';
import { useKb } from './vendor-knowledge-screen';

/**
 * An implementation partner (prototype `partnerView()`): the products it
 * implements and its requirement answers, grouped by product. Partners
 * have no web domains or sources of their own: their answers came from the
 * Caliber v1 import.
 */
export function PartnerPage({ partnerId }: { partnerId: string }) {
  const { tree, navigate } = useKb();
  const partner = tree.partners.find((p) => p.id === partnerId)!;
  const summary = useKbSummary({ partner_id: partnerId });
  const products = partner.product_ids
    .map((id) => {
      const vendor = tree.vendors.find((v) => v.products.some((p) => p.id === id));
      const product = vendor?.products.find((p) => p.id === id);
      return product && vendor
        ? { id, name: product.name, vendorId: vendor.id, vendorName: vendor.name }
        : null;
    })
    .filter(
      (p): p is { id: string; name: string; vendorId: string; vendorName: string } => p !== null,
    );

  // Products this partner answered for, from the server's module counts.
  const answered = new Set(
    (summary.data?.modules ?? []).filter((m) => m.requirement_answers > 0).map((m) => m.product_id),
  ).size;

  return (
    <Page>
      <PageHead title={partner.name} eyebrow="Implementation partner" />
      <div className="mt-4">
        <Facts>
          <Fact label="Products they implement">
            {products.length === 0 ? (
              <span className="text-meta text-ink-500">None recorded</span>
            ) : (
              products.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => navigate({ kind: 'vendor', id: p.vendorId })}
                  className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600"
                  title={`Open ${p.vendorName}`}
                >
                  <DChip sub={p.vendorName !== p.name ? p.vendorName : undefined}>{p.name}</DChip>
                </button>
              ))
            )}
          </Fact>
        </Facts>
      </div>
      {summary.isPending ? (
        <div className="mt-6">
          <Spinner />
        </div>
      ) : summary.isError ? (
        <ErrorMessage error={summary.error} />
      ) : (
        <>
          <SummaryLine
            parts={
              summary.data.requirement_answers > 0
                ? [
                    <span key="a">
                      <B>{fmt(summary.data.requirement_answers)}</B> requirement answers
                    </span>,
                    <span key="p">for {plural(answered, 'product')}</span>,
                    <span key="s">Avero Advisors Vendor Knowledge Base</span>,
                  ]
                : [
                    <span key="n">No requirement answers yet</span>,
                    <span key="p">{plural(products.length, 'product')} implemented</span>,
                  ]
            }
          />
          {summary.data.requirement_answers > 0 ? (
            <AnswersSection
              scope={{ kind: 'partner', id: partnerId, name: partner.name }}
              summary={summary.data}
              products={products}
              defaultOpen
            />
          ) : (
            <Section label="Requirement answers">
              <SectionHead title="Requirement answers" count={0} />
              <Empty small>
                No requirement answers yet. Answers came from the Caliber v1 import.
              </Empty>
            </Section>
          )}
        </>
      )}
    </Page>
  );
}
